import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gauge, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const zoneColor = (v) => {
  if (v <= 24) return '#EF4444';
  if (v <= 44) return '#F97316';
  if (v <= 55) return '#EAB308';
  if (v <= 75) return '#84CC16';
  return '#10B981';
};

const GaugeArc = ({ value, size = 260 }) => {
  const deg = (value / 100) * 180;
  const seg = (a, b, color) => `${color} ${a * 1.8}deg ${b * 1.8 - 1.5}deg, transparent ${b * 1.8 - 1.5}deg ${b * 1.8}deg`;
  return (
    <div className="relative mx-auto" style={{ width: size, maxWidth: '100%', height: size / 2 + 24 }} data-testid="fg-arc">
      <div className="absolute inset-x-0 top-0 overflow-hidden" style={{ height: size / 2 }}>
        <div
          style={{
            width: size, height: size, borderRadius: '50%',
            background: `conic-gradient(from 270deg, ${seg(0, 24, '#EF4444')}, ${seg(24, 44, '#F97316')}, ${seg(44, 56, '#EAB308')}, ${seg(56, 76, '#84CC16')}, ${seg(76, 100, '#10B981')} , transparent 180deg 360deg)`,
            WebkitMask: 'radial-gradient(circle at center, transparent 61%, black 62%)',
            mask: 'radial-gradient(circle at center, transparent 61%, black 62%)',
          }}
        />
      </div>
      {/* Needle */}
      <div
        className="absolute left-1/2"
        style={{
          top: size / 2 - size * 0.34, height: size * 0.34, width: 3, marginLeft: -1.5,
          background: '#C5A059', borderRadius: 2, transformOrigin: 'bottom center',
          transform: `rotate(${deg - 90}deg)`, transition: 'transform 1s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      />
      <div className="absolute left-1/2 rounded-full bg-[#C5A059]"
        style={{ top: size / 2 - 7, width: 14, height: 14, marginLeft: -7 }} />
      {/* Value */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center" style={{ top: size / 2 - size * 0.46 }}>
        <span className="font-manrope font-extrabold" style={{ color: zoneColor(value), fontSize: size * 0.15 }} data-testid="fg-value">
          {value}
        </span>
      </div>
      <div className="absolute text-[9px] font-bold tracking-widest text-red-400" style={{ left: 4, top: size / 2 + 6 }}>FEAR</div>
      <div className="absolute text-[9px] font-bold tracking-widest text-emerald-400" style={{ right: 4, top: size / 2 + 6 }}>GREED</div>
    </div>
  );
};

const Sparkline = ({ history }) => {
  if (!history?.length) return null;
  const w = 280, h = 48;
  const vals = history.map((d) => d.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const pts = vals.map((v, i) => {
    const x = (i / (vals.length - 1)) * w;
    const y = h - ((v - min) / Math.max(1, max - min)) * (h - 8) - 4;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" data-testid="fg-sparkline">
      <polyline points={pts} fill="none" stroke="#C5A059" strokeWidth="1.5" opacity="0.8" />
    </svg>
  );
};

export const FearGreedGauge = ({ compact = false }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    axios.get(`${API}/market/fear-greed`)
      .then((r) => setData(r.data))
      .catch(() => setError(true));
  }, []);

  if (error) return null;
  if (!data) {
    return (
      <div className="flex items-center justify-center py-10" data-testid="fear-greed-loading">
        <RefreshCw className="animate-spin text-[#C5A059]" size={22} />
      </div>
    );
  }

  const color = zoneColor(data.value);

  if (compact) {
    return (
      <div className="bg-[#111111] border border-white/10 rounded-xl p-5" data-testid="fear-greed-compact">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Gauge size={15} className="text-[#C5A059]" />
            <span className="text-gray-400 text-xs font-semibold uppercase tracking-wider">Fear &amp; Greed</span>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#C5A059]">Buy the Fear · Sell the Greed</span>
        </div>
        <div className="flex items-center gap-5">
          <div className="w-36 shrink-0"><GaugeArc value={data.value} size={150} /></div>
          <div>
            <p className="font-manrope font-extrabold text-xl" style={{ color }}>{data.classification}</p>
            <p className="text-gray-500 text-xs mt-1">{data.signal.zone} — <span style={{ color }} className="font-bold">{data.signal.action}</span></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-[#111111] to-[#0A0A0A] border border-[#C5A059]/25 rounded-2xl overflow-hidden" data-testid="fear-greed-gauge">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        {/* Left: gauge */}
        <div className="p-8 flex flex-col items-center justify-center border-b lg:border-b-0 lg:border-r border-white/10">
          <div className="w-full max-w-[300px]"><GaugeArc value={data.value} /></div>
          <p className="font-manrope font-extrabold text-2xl -mt-2" style={{ color }} data-testid="fg-classification">
            {data.classification}
          </p>
          <div className="mt-3 px-4 py-1.5 rounded-full border" style={{ borderColor: `${color}66`, background: `${color}14` }}>
            <span className="text-xs font-bold uppercase tracking-widest" style={{ color }}>
              {data.signal.zone} · {data.signal.action}
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-3 text-center max-w-xs">{data.signal.note}</p>
        </div>

        {/* Right: components + history */}
        <div className="p-8">
          <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-4">Institutional Components</h4>
          <div className="space-y-4 mb-6">
            {data.components.map((c) => (
              <div key={c.key} data-testid={`fg-component-${c.key}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-300 text-sm">{c.label} <span className="text-gray-600 text-[10px]">({c.weight}%)</span></span>
                  <span className="text-sm font-bold font-mono" style={{ color: zoneColor(c.value) }}>{c.value}</span>
                </div>
                <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${c.value}%`, background: zoneColor(c.value) }} />
                </div>
                <p className="text-gray-600 text-[10px] mt-0.5">{c.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between mb-1">
            <h4 className="text-gray-400 text-xs font-semibold uppercase tracking-wider">30-Day Trend</h4>
            <span className="text-gray-600 text-[10px]">BTC 24h: <span className={data.btc_change_24h >= 0 ? 'text-emerald-400' : 'text-red-400'}>{data.btc_change_24h >= 0 ? '+' : ''}{data.btc_change_24h}%</span></span>
          </div>
          <Sparkline history={data.history} />
        </div>
      </div>
    </div>
  );
};

export default FearGreedGauge;
